/**
 * x-swatch-kit-form directive.
 *
 * Manages a multi-step swatch kit order form. Persists selected swatches and
 * contact form data to localStorage across page refreshes. On submission,
 * serialises the swatch selection alongside the contact form fields into URL
 * search parameters and submits via a GET request.
 *
 * Sub-directive:
 *   x-swatch-kit-form:step - Applied to each step element. Accepts an Alpine
 *                            expression that evaluates to a boolean representing
 *                            whether the step is complete. Registers the step
 *                            with the nearest controller via `$swatchKitForm`.
 *
 * @options (root only)
 *   limit - Maximum number of swatches a user may select (default: 12).
 *
 * @magic
 *   $swatchKitForm - Returns the reactive step controller for the nearest root.
 *
 * @example
 *   <form x-swatch-kit-form="{ limit: 6 }">
 *     <div x-swatch-kit-form:step="hasSwatchSelection">Step 1</div>
 *     <div x-swatch-kit-form:step="validInformationDetails">Step 2</div>
 *   </form>
 */
(() => {
  /** @type {WeakMap<Element, object>} Maps Alpine root elements to their step controllers. */
  const Controllers = new WeakMap();

  /**
   * Converts a FormData object to a plain JavaScript object. Multi-value keys
   * are kept as arrays; single-value keys are unwrapped to their scalar value.
   * @param {FormData} formData - The FormData instance to convert.
   * @returns {Record<string, string|string[]>} The plain object representation.
   */
  const formDataToObject = (formData) => {
    const object = {};

    formData.forEach((value, key) => {
      if (!object[key]) {
        object[key] = [];
      }
      object[key].push(value);
    });

    // Convert single-item arrays to scalar values.
    Object.keys(object).forEach((key) => {
      if (object[key].length === 1) {
        object[key] = object[key][0];
      }
    });

    return object;
  };

  const root = (Alpine, el, { limit = 12 }) => {
    Alpine.bind(el, () => ({
      ["@submit.prevent"]() {
        this.handleSubmission();
      },

      /** Adds or removes a swatch when a swatch checkbox changes. */
      ["@update-selection.stop"]($event) {
        const { data } = $event.detail;

        if ($event.target.checked) {
          this.addSwatch(data);
        } else {
          this.removeSwatch(data);
        }
      },

      /** Unchecks the swatch input and removes it from the selection. */
      ["@remove-selection.stop"]($event) {
        const { swatch } = $event.detail;

        const input = this.$root.querySelector(`#SwatchKit_${swatch.id}_Input`);

        if (input) {
          input.checked = false;
          input.setAttribute("checked", false);
        }

        this.removeSwatch(swatch);
      },

      ["x-data"]() {
        return {
          __loading: false,
          __submissionError: false,
          __swatches: Alpine.$persist([]).as("swatch_kit_form_swatches"),
          __formHasErrors: false,
          __formValidated: false,
          __formData: null,
          __completedOrder: false,
          __persistedFormObject: Alpine.$persist({}).as(
            "swatch_kit_form_object",
          ),

          /**
           * Restores persisted swatch selections and form field values. Removes
           * any persisted swatches whose input elements no longer exist in the DOM.
           * In Shopify design mode, also listens for block-select events to navigate
           * between steps.
           */
          init() {
            if (this.__swatches.length) {
              this.__swatches = this.__swatches.filter((swatch) => {
                const input = this.$root.querySelector(
                  `#SwatchKit_${swatch.id}_Input`,
                );

                if (input) {
                  input.checked = true;
                  input.setAttribute("checked", true);

                  return true;
                }

                return false;
              });
            }

            // Populate form from persisted data.
            if (
              this.__persistedFormObject &&
              Object.keys(this.__persistedFormObject).length > 0
            ) {
              const form = this.$root.querySelector("[x-form]");

              if (form) {
                const xForm = Alpine.$data(form);

                if (xForm && typeof xForm.populateForm === "function") {
                  xForm.populateForm(this.__persistedFormObject);
                }
              }
            }

            // Shopify theme editor: navigate to step when block is selected.
            if (window.Shopify && window.Shopify.designMode) {
              const section = this.$root.closest("section");

              if (section) {
                section.addEventListener("shopify:block:select", (event) => {
                  const blockEl = document.getElementById(event.detail.blockId);

                  if (!blockEl) return;

                  // Step sequence nav: only navigate back if at or before current.
                  const navEl = blockEl.closest("[data-swatch-kit-step-nav]");

                  if (navEl) {
                    const target = parseInt(navEl.dataset.swatchKitStepNav, 10);

                    if (target <= this.$swatchKitForm.current_index) {
                      this.$swatchKitForm.goto(target);
                    }

                    return;
                  }

                  // Main step blocks: navigate unconditionally.
                  const stepEl = blockEl.closest("[data-swatch-kit-step]");

                  if (stepEl) {
                    this.$swatchKitForm.goto(
                      parseInt(stepEl.dataset.swatchKitStep, 10),
                    );
                  }
                });
              }
            }
          },

          /**
           * Serialises the swatch selection and form fields into URL search
           * parameters and submits via GET. Advances to the next step on success.
           * No-ops in Shopify design mode.
           * @returns {Promise<void>}
           */
          async handleSubmission() {
            if (window.Shopify && window.Shopify.designMode) {
              return;
            }

            this.__loading = true;
            this.__completedOrder = false;
            this.__submissionError = false;

            const url = this.$root.action;

            const formObject = this.__swatches.reduce(
              (data, swatch) => {
                data.Swatches.push(`${swatch.collection} - ${swatch.name}`);
                data.Quantity.push(1);
                data.Price.push(0);
                data.SKU.push(swatch.sku);

                return data;
              },
              {
                Swatches: [],
                Quantity: [],
                Price: [],
                SKU: [],
                ...formDataToObject(new FormData(this.$root)),
              },
            );

            // Remove collection keys from formObject.
            this.__swatches.forEach(({ collection }) => {
              delete formObject[collection];
            });

            // Generate a unique order ID.
            formObject.orderNumber = `SKO-${Date.now().toString()}`;

            // Append UTM and tracking parameters from the current URL.
            const params = [
              "utm_source",
              "utm_medium",
              "utm_campaign",
              "utm_term",
              "utm_content",
              "tw_source",
              "tw_adid",
            ];

            const searchParams = new URLSearchParams(window.location.search);

            params.forEach((param) => {
              const paramVal = searchParams.get(param);

              if (paramVal) {
                formObject[param] = paramVal;
              }
            });

            const submissionSearchParams = new URLSearchParams(
              formObject,
            ).toString();

            const response = await fetch(`${url}/?${submissionSearchParams}`, {
              method: "GET",
            });

            this.__loading = false;

            if (response.ok) {
              this.__completedOrder = true;
              this.__swatches = [];
            } else {
              this.__submissionError = true;
            }

            this.$swatchKitForm.canGoForward() && this.$swatchKitForm.forward();
          },

          /**
           * Adds a swatch to the selection if the limit has not been reached
           * and the swatch is not already selected.
           * @param {{ id: string }} data - The swatch data object.
           */
          addSwatch(data) {
            if (this.swatchSelectionReachedLimit) {
              return;
            }

            const found = this.__swatches.find(
              (swatch) => swatch.id === data.id,
            );

            if (found) {
              return;
            }

            this.__swatches.push(data);
          },

          /**
           * Removes a swatch from the selection by ID.
           * @param {{ id: string }} swatch - The swatch to remove.
           */
          removeSwatch({ id }) {
            const swatches = this.__swatches.filter(
              (swatch) => swatch.id !== id,
            );

            this.__swatches = swatches;
          },

          /**
           * Validates the contact information form, persists the data, and
           * updates the form-has-errors and form-validated flags.
           * @returns {Promise<void>}
           */
          async handleInformationDetails() {
            const form = this.$root.querySelector("[x-form]");

            if (!form) {
              return;
            }

            const xForm = Alpine.$data(form);
            await xForm.validateForm({
              scope: this.$el,
            });

            this.__formValidated = true;
            this.__formHasErrors = xForm.hasErrors;
            this.__formData = xForm.getFormData();

            // Persist form data to localStorage.
            this.__persistedFormObject = this.formDataToObject;

            if (this.__formHasErrors) {
              return;
            }
          },

          /**
           * Returns the persisted form data object from localStorage.
           * @returns {Record<string, string|string[]>}
           */
          get persistedFormObject() {
            return this.__persistedFormObject;
          },

          /**
           * Converts the current form data to a plain object.
           * Returns an empty object when no form data has been captured yet.
           * @returns {Record<string, string|string[]>}
           */
          get formDataToObject() {
            if (!this.__formData) {
              return {};
            }

            return formDataToObject(this.__formData);
          },

          /**
           * Returns the selected swatches enriched with thumbnail image attributes
           * from their corresponding DOM elements.
           * @returns {Array<object>}
           */
          get swatches() {
            return this.__swatches.map((swatch) => {
              let thumbnail = {};

              const image = this.$root.querySelector(
                `#SwatchKit_${swatch.id} img`,
              );

              if (image) {
                Array.from(image.attributes).forEach((attribute) => {
                  thumbnail[attribute.name] = attribute.value;
                });
              }

              return {
                ...swatch,
                thumbnail,
              };
            });
          },

          /**
           * Returns true when at least one swatch has been selected.
           * @returns {boolean}
           */
          get hasSwatchSelection() {
            return this.__swatches.length > 0;
          },

          /**
           * Returns true when the form is on the first step (swatches are editable).
           * @returns {boolean}
           */
          get isSwatchEditable() {
            return this.$swatchKitForm.isFirst();
          },

          /**
           * Returns true when the swatch selection has reached the configured limit.
           * @returns {boolean}
           */
          get swatchSelectionReachedLimit() {
            return limit ? this.__swatches.length >= limit : false;
          },

          /**
           * Returns the number of currently selected swatches.
           * @returns {number}
           */
          get swatchSelectionCount() {
            return this.__swatches.length;
          },

          /**
           * Returns true when the contact information form has been validated
           * without errors.
           * @returns {boolean}
           */
          get validInformationDetails() {
            return this.__formValidated && !this.__formHasErrors;
          },

          /**
           * Returns true when the last form submission resulted in an error.
           * @returns {boolean}
           */
          get hasSubmissionErrors() {
            return this.__submissionError;
          },

          /**
           * Returns true when the last form submission completed successfully
           * without errors.
           * @returns {boolean}
           */
          get completedOrder() {
            return this.__completedOrder && !this.__submissionError;
          },
        };
      },
    }));
  };

  /**
   * Finds the next applicable step index from `current` in the given direction.
   * @param {object[]} steps - The array of step objects.
   * @param {number} current - The current step index.
   * @param {1|-1} [direction=1] - Search direction: 1 for forward, -1 for back.
   * @returns {number|null} The next applicable index, or null if none exists.
   */
  const findNextIndex = (steps, current, direction = 1) => {
    for (
      let index = current + direction;
      index >= 0 && index < steps.length;
      index += direction
    ) {
      if (steps[index] && steps[index].is_applicable) {
        return index;
      }
    }

    return null;
  };

  /**
   * Returns the step controller for the closest Alpine root element, creating
   * one if it does not yet exist.
   * @param {object} Alpine - The Alpine.js instance.
   * @param {Element} el - Any element inside the root.
   * @returns {object} The reactive step controller.
   */
  const getController = (Alpine, el) => {
    const root = Alpine.closestRoot(el);

    if (!Controllers.has(root)) {
      Controllers.set(root, createController(Alpine));
    }

    return Controllers.get(root);
  };

  /**
   * Creates a new reactive step controller with navigation and progress helpers.
   * @param {object} Alpine - The Alpine.js instance.
   * @returns {object} The reactive step controller.
   */
  const createController = (Alpine) => {
    return Alpine.reactive({
      steps: [],

      current_index: 0,

      /**
       * Returns the current step object, or a default empty step.
       * @returns {{ el: Element|null, title: string|null }}
       */
      current() {
        return this.steps[this.current_index] || { el: null, title: null };
      },

      /**
       * Returns the next applicable step object, or a default empty step.
       * @returns {{ el: Element|null, title: string|null }}
       */
      next() {
        return this.steps[this.nextIndex()] || { el: null, title: null };
      },

      /**
       * Returns the previous applicable step object, or a default empty step.
       * @returns {{ el: Element|null, title: string|null }}
       */
      previous() {
        return this.steps[this.previousIndex()] || { el: null, title: null };
      },

      /**
       * Returns the index of the nearest previous applicable step, or null.
       * @returns {number|null}
       */
      previousIndex() {
        return findNextIndex(this.steps, this.current_index, -1);
      },

      /**
       * Returns the index of the nearest next applicable step, or null.
       * @returns {number|null}
       */
      nextIndex() {
        return findNextIndex(this.steps, this.current_index, 1);
      },

      /**
       * Calculates the current progress across all applicable steps.
       * Values are computed dynamically from the `is_applicable` and
       * `is_complete` flags to reflect the user's current state.
       * @returns {{ total: number, complete: number, current: number, incomplete: number, percentage: string, percentage_int: number, percentage_float: number }}
       */
      progress() {
        let current = 0;
        let complete = 0;
        let total = 0;

        for (let index = 0; index < this.steps.length; index++) {
          const step = this.steps[index];

          if (!step.is_applicable) {
            continue;
          }

          total++;

          if (index <= this.current_index) {
            current++;
          }

          if (index <= this.current_index && step.is_complete) {
            complete++;
          }
        }

        return {
          total,
          complete,
          current,
          incomplete: total - complete,
          percentage: `${Math.floor((complete / total) * 100)}%`,
          percentage_int: Math.floor((complete / total) * 100),
          percentage_float:
            Math.floor((complete / total + Number.EPSILON) * 100) / 100,
        };
      },

      /**
       * Returns true when there is no previous applicable step.
       * @returns {boolean}
       */
      isFirst() {
        return null === this.previousIndex();
      },

      /**
       * Returns true when there is a previous applicable step.
       * @returns {boolean}
       */
      isNotFirst() {
        return !this.isFirst();
      },

      /**
       * Returns true when there is no next applicable step.
       * @returns {boolean}
       */
      isLast() {
        return null === this.nextIndex();
      },

      /**
       * Returns true when there is a next applicable step.
       * @returns {boolean}
       */
      isNotLast() {
        return !this.isLast();
      },

      /**
       * Returns true when the current step is complete and there are no further steps.
       * @returns {boolean}
       */
      isComplete() {
        return this.current().is_complete && null === this.nextIndex();
      },

      /**
       * Returns true when the form has not yet been completed.
       * @returns {boolean}
       */
      isNotComplete() {
        return !this.isComplete();
      },

      /**
       * Alias for `isNotComplete()`.
       * @returns {boolean}
       */
      isIncomplete() {
        return this.isNotComplete();
      },

      /**
       * Returns true when the current step is complete and a next step exists.
       * @returns {boolean}
       */
      canGoForward() {
        return this.current().is_complete && null !== this.nextIndex();
      },

      /**
       * Returns true when the user cannot advance to the next step.
       * @returns {boolean}
       */
      cannotGoForward() {
        return !this.canGoForward();
      },

      /**
       * Advances to the next applicable step.
       */
      forward() {
        this.goto(this.nextIndex());
      },

      /**
       * Returns true when a previous applicable step exists.
       * @returns {boolean}
       */
      canGoBack() {
        return null !== this.previousIndex();
      },

      /**
       * Returns true when there is no previous step to return to.
       * @returns {boolean}
       */
      cannotGoBack() {
        return !this.canGoBack();
      },

      /**
       * Returns to the previous applicable step.
       */
      back() {
        this.goto(this.previousIndex());
      },

      /**
       * Navigates to the step at the given index. No-ops when the index is
       * null or out of bounds.
       * @param {number|null} index - The target step index.
       * @returns {{ el: Element|null, title: string|null }} The current step after navigation.
       */
      goto(index) {
        if (null !== index && undefined !== this.steps[index]) {
          this.current_index = index;
        }

        return this.current();
      },

      /**
       * Returns the reactive step object for the given element, creating it if
       * it does not yet exist. Also sets the `x-show` binding on the element so
       * only the active step is visible.
       * @param {Element} el - The step element.
       * @returns {object} The reactive step object.
       */
      getStep(el) {
        let step = this.steps.find((step) => step.el === el);

        if (!step) {
          // Enable the step visibility logic.
          el.setAttribute("x-show", "$swatchKitForm.current().el === $el");

          step = Alpine.reactive({
            el,
            title: "",
            is_applicable: true,
            is_complete: true,
            errors: {},
            cleanup: () => {
              this.steps = this.steps.filter((step) => step.el !== el);
            },
          });

          this.steps.push(step);
        }

        return step;
      },
    });
  };

  document.addEventListener("alpine:init", () => {
    Alpine.directive(
      "swatch-kit-form",
      (el, { value, expression }, { evaluate }) => {
        const options = expression ? evaluate(expression) : {};

        if ("step" === value) {
          const controller = getController(Alpine, el);
          const current = controller.getStep(el);

          const evaluateCheck = () => [!!evaluate(expression), {}];

          Alpine.effect(() => {
            if ("" !== expression) {
              const [passes, errors] = evaluateCheck();
              current.is_complete = passes;
              current.errors = errors;
            }
          });
        } else {
          root(Alpine, el, options);
        }
      },
    ).before("bind");

    Alpine.magic("swatchKitForm", (el, { Alpine }) => {
      return getController(Alpine, el);
    });
  });
})();
